import React, { useState } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import { DesktopLayout, StandingsSidebar, LiveAnswer as SharedLiveAnswer, TimerSection } from '../shared';
import type { GameStatistics } from '../../types/game';
import { usePartyHostGameLifecycle } from '../../hooks/usePartyHostGameLifecycle';
import { renderAvatar } from '../../utils/avatarUtils';
import {
  PartyHostGameEnd,
  PartyHostPlayingChrome,
  PartyHostResultFrame,
  PartyHostWaiting,
} from './PartyHostShell';
import { partyHostCssTheme } from '../../themes/cssTheme';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface DrinkingGameQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  sipPenalty?: number;
  funFact?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  isDrinkingGame?: boolean;
}

interface SipResultEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  sips: number;
  wrongAnswers: number;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  sipResults?: SipResultEntry[];
  sipPenalty?: number;
  funFact?: string;
  difficulty?: string;
  isDrinkingGame?: boolean;
}

interface LiveAnswer {
  playerId: string;
  playerName: string;
  avatar?: string;
  timestamp: number;
  answer?: string; // The selected answer/option
}

interface DrinkingGameHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: DrinkingGameQuestion;
    finalLeaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    gameStatistics?: GameStatistics | null;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

const THEME = partyHostCssTheme;

const DrinkingGameHostDisplay: React.FC<DrinkingGameHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  myPlayerId,
  hostIsPlaying = false,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [sipsEarned, setSipsEarned] = useState(0);
  const {
    currentQuestion,
    timeRemaining,
    questionResult,
    gameEnded,
    finalLeaderboard,
    gameStatistics,
    waitingForNewGame,
    liveAnswers,
    answerSubmitted,
    gameEndData,
    playersRef,
    submitAnswer,
    restartGame: handleRestartGame,
    playSameGame,
    setAnswerSubmitted,
  } = usePartyHostGameLifecycle<
    DrinkingGameQuestion,
    QuestionResult,
    { questionId: string; isCorrect: boolean; points: number; sipsEarned?: number }
  >({
    roomState,
    socket,
    hostIsPlaying,
    isQuestionForGame: (question) => !!question.isDrinkingGame,
    isResultForGame: (result) => !!result.isDrinkingGame,
    onResetHostPlaying: () => {
      setSelectedAnswer(null);
      setAnswerCorrect(null);
      setPointsEarned(0);
      setSipsEarned(0);
    },
    onAnswerReceived: (data) => {
      if (!hostIsPlaying) return;
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      setSipsEarned(data.sipsEarned || 0);
      setAnswerSubmitted(true);
    },
  });

  const handleAnswerClick = (answer: string) => {
    if (answerSubmitted || !currentQuestion) return;
    setSelectedAnswer(answer);
    if (!submitAnswer(answer)) setSelectedAnswer(null);
  };

  const getPlayerAvatar = (playerId: string): string | undefined => {
    const player = playersRef.current.find((p) => p.id === playerId);
    return player?.avatar;
  };

  if (gameEnded) {
    return (
      <PartyHostGameEnd
        roomName={roomState.roomName}
        gameType={roomState.gameType}
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        gameEndData={gameEndData}
        onPlaySameGame={playSameGame}
        onBackToGameSelection={onBackToGameSelection}
        getPlayerAvatar={getPlayerAvatar}
      />
    );
  }

  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <PartyHostWaiting
        roomName={roomState.roomName}
        emoji="🍺"
        onBackToGameSelection={onBackToGameSelection}
      >
        <div className="party-host-waiting__bounce" aria-hidden>
          <span className="party-host-waiting__bounce-item">🍻</span>
          <span className="party-host-waiting__bounce-item">🍷</span>
          <span className="party-host-waiting__bounce-item">🥃</span>
        </div>
      </PartyHostWaiting>
    );
  }

  // Question result screen - fullscreen who needs to drink
  if (questionResult) {
    const sipResults = questionResult.sipResults || [];
    const hasDrinkers = sipResults.length > 0;

    const resultSharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map((la) => ({
      playerId: la.playerId,
      playerName: la.playerName,
      avatar: la.avatar,
      timestamp: la.timestamp,
      answer: la.answer !== undefined ? String(la.answer) : undefined,
    }));

    return (
      <PartyHostResultFrame roomName={roomState.roomName} onBackToGameSelection={onBackToGameSelection}>
        <DesktopLayout
          leftContent={
              <div className="result-grid" style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '2rem',
                maxWidth: '1400px',
                margin: '0 auto',
                width: '100%',
              }}>
                {/* Correct Answer */}
                <div className="correct-answer-card" style={{
                  background: 'rgba(52, 211, 153, 0.15)',
                  border: '3px solid rgba(52, 211, 153, 0.3)',
                  borderRadius: '1.5rem',
                  padding: 'clamp(1.5rem, 4vw, 2rem)',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', opacity: 0.7, marginBottom: '0.5rem', color: '#fff' }}>{t('gameplay.correctAnswerLabel')}</div>
                  <div className="correct-answer-text" style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', fontWeight: 'bold', color: '#34D399' }}>
                    {questionResult.correctAnswer}
                  </div>
                </div>

                {/* Who needs to drink */}
                <div className="drinking-section" style={{
                  background: hasDrinkers ? THEME.primaryLight : 'rgba(52, 211, 153, 0.1)',
                  border: `3px solid ${hasDrinkers ? THEME.primaryBorder : 'rgba(52, 211, 153, 0.3)'}`,
                  borderRadius: '1.5rem',
                  padding: 'clamp(1.5rem, 4vw, 2rem)',
                  paddingBottom: '5rem',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  <h2 className="drinking-title" style={{
                    textAlign: 'center',
                    marginBottom: '2rem',
                    color: hasDrinkers ? THEME.primary : '#34D399',
                    fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                  }}>
                    {hasDrinkers ? '🍺 Time to Drink! 🍺' : '🎉 Nobody Drinks! 🎉'}
                  </h2>
                  
                  {hasDrinkers ? (
                    <div className="drinkers-grid" style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
                      gap: '1rem',
                      marginBottom: '5rem',
                    }}>
                      {sipResults.map((entry, index) => (
                        <div
                          key={entry.playerId}
                          className="drinker-card"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            padding: '1.25rem',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '1rem',
                            animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
                          }}
                        >
                          {renderAvatar(entry.avatar, 'clamp(2rem, 5vw, 3rem)')}
                          <span className="drinker-name" style={{ flex: 1, fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', fontWeight: 'bold', color: '#fff' }}>
                            {entry.playerName}
                          </span>
                          <div className="sip-badge" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            background: THEME.primary,
                            padding: '0.75rem 1.25rem',
                            borderRadius: '0.75rem',
                            fontWeight: 'bold',
                            fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                          }}>
                            <span style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>
                              {entry.sips === 1 ? '🍺' : entry.sips === 2 ? '🍺🍺' : '🍺🍺🍺'}
                            </span>
                            <span>{entry.sips} {entry.sips === 1 ? 'sip' : 'sips'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', opacity: 0.8, marginBottom: '5rem', color: '#fff' }}>
                      Everyone got it right! 🎊
                    </div>
                  )}

                  {/* Animated beer ticker at bottom - seamless loop */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0.5rem',
                    left: 0,
                    right: 0,
                    height: '5.5rem',
                    paddingTop: '1rem',
                    paddingBottom: '0.5rem',
                    overflow: 'hidden',
                    marginTop: '2rem',
                  }}>
                    <div className="beer-ticker" style={{
                      display: 'flex',
                      width: 'max-content',
                      animation: 'beerTicker 20s linear infinite',
                      alignItems: 'center',
                    }}>
                      {/* First set of emojis */}
                      {['🍺', '🍻', '🥂', '🍺', '🥃', '🍷', '🍺', '🍻', '🍺', '🥂', '🍾', '🍺', '🍻', '🥃', '🍺', '🍷', '🍺', '🍻', '🥂', '🍺'].map((emoji, i) => (
                        <span 
                          key={`a-${i}`}
                          className="beer-bounce" 
                          style={{ 
                            fontSize: '2rem', 
                            padding: '0 1rem',
                            animationDelay: `${i * 0.15}s`,
                          }}
                        >
                          {emoji}
                        </span>
                      ))}
                      {/* Duplicate set for seamless loop */}
                      {['🍺', '🍻', '🥂', '🍺', '🥃', '🍷', '🍺', '🍻', '🍺', '🥂', '🍾', '🍺', '🍻', '🥃', '🍺', '🍷', '🍺', '🍻', '🥂', '🍺'].map((emoji, i) => (
                        <span 
                          key={`b-${i}`}
                          className="beer-bounce" 
                          style={{ 
                            fontSize: '2rem', 
                            padding: '0 1rem',
                            animationDelay: `${(i + 20) * 0.15}s`,
                          }}
                        >
                          {emoji}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Fun Fact */}
                {questionResult.funFact && (
                  <div className="fun-fact-card" style={{
                    background: THEME.accentLight,
                    border: `3px solid ${THEME.accentBorder}`,
                    borderRadius: '1.5rem',
                    padding: 'clamp(1.2rem, 3vw, 1.5rem)',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                      fontWeight: 'bold',
                      color: THEME.accent,
                      fontSize: 'clamp(1.15rem, 2.8vw, 1.4rem)',
                    }}>
                      <span>💡</span>
                      <span>{t('gameplay.drinkingGame.didYouKnow')}</span>
                    </div>
                    <div className="fun-fact-text" style={{ fontSize: 'clamp(1.15rem, 2.8vw, 1.4rem)', lineHeight: '1.6', color: '#fff' }}>
                      {questionResult.funFact}
                    </div>
                  </div>
                )}
              </div>
            }
            rightContent={
              <StandingsSidebar
                players={roomState.players}
                liveAnswers={resultSharedLiveAnswers}
                totalPlayers={roomState.players.length}
                questionId={questionResult.questionId}
                shouldUpdateScores={true}
                theme={THEME}
              />
            }
            mobileOnlyContent={
              <StandingsSidebar
                players={roomState.players}
                liveAnswers={resultSharedLiveAnswers}
                totalPlayers={roomState.players.length}
                questionId={questionResult.questionId}
                shouldUpdateScores={true}
                isMobile={true}
                theme={THEME}
              />
            }
            rightWidth="320px"
          />
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes beerTicker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes beerBounce {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            25% { transform: translateY(-12px) rotate(-8deg); }
            75% { transform: translateY(-12px) rotate(8deg); }
          }
          .beer-bounce {
            display: inline-block;
            animation: beerBounce 0.5s ease-in-out infinite;
          }
          .beer-ticker {
            will-change: transform;
          }
          
          @media (max-width: 1024px) {
            .result-grid { grid-template-columns: 1fr !important; }
            .drinking-title { font-size: 2rem !important; }
            .correct-answer-text { font-size: 2rem !important; }
            .correct-answer-card { padding: 1.5rem !important; }
            .drinking-section { padding: 1.5rem !important; }
            .drinker-card { padding: 1rem !important; }
            .drinker-name { font-size: 1.2rem !important; }
            .sip-badge { padding: 0.5rem 1rem !important; font-size: 1rem !important; }
            .sip-badge span:first-child { font-size: 1.5rem !important; }
            .fun-fact-card { padding: 1.25rem !important; }
            .fun-fact-text { font-size: 1rem !important; }
          }
          
          @media (max-width: 600px) {
            .drinking-title { font-size: 1.5rem !important; }
            .correct-answer-text { font-size: 1.5rem !important; }
            .drinkers-grid { grid-template-columns: 1fr !important; }
            .drinker-name { font-size: 1rem !important; }
          }
        `}</style>
      </PartyHostResultFrame>
    );
  }

  // Question display - fullscreen
  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  // Convert liveAnswers to shared format
  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(la => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer,
  }));

  return (
    <PartyHostPlayingChrome
      gameLabel="🍺 Drinking Game"
      roomName={roomState.roomName}
      theme={THEME}
      onBackToGameSelection={onBackToGameSelection}
      onRestart={handleRestartGame}
    >
      <div style={{
        minHeight: 'calc(100vh - 82px)',
        width: '100%',
        padding: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}>
        <DesktopLayout
          leftContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Main Challenge Display */}
        <div 
          className="container game-display-container" 
          style={{ 
            paddingTop: '1rem',
            paddingBottom: '1rem',
            marginTop: '1rem',
            marginBottom: 0,
          }}
        >
          <div className="timer-section" style={{ width: '100%', marginBottom: '1.2rem' }}>
            <TimerSection
              questionNumber={currentQuestion?.questionNumber}
              totalQuestions={currentQuestion?.totalQuestions}
              timeSeconds={secondsRemaining}
              progress={progress}
              primaryColor={THEME.primary}
            />
          </div>

          {/* Question text - using same font size as trivia game */}
          <h1 className="game-question-text" style={{
            color: THEME.primary,
            marginBottom: '1.5rem',
            letterSpacing: '0.03em',
            textShadow: `0 2px 8px ${THEME.primaryBorder}`,
          }}>
            {currentQuestion?.question}
          </h1>

          {/* Options – only when host is playing */}
          {hostIsPlaying && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              marginTop: '1rem',
            }}>
              {currentQuestion?.options.map((option, index) => {
                const isSelected = selectedAnswer === option;
                const showResult = answerSubmitted && answerCorrect !== null;
                const isCorrectAnswer = showResult && answerCorrect && isSelected;
                const isWrongAnswer = showResult && !answerCorrect && isSelected;

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerClick(option)}
                    disabled={answerSubmitted}
                    className="game-option-text"
                    style={{
                      padding: '1rem 0.8rem',
                      borderRadius: '12px',
                      cursor: answerSubmitted ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      border: isSelected ? `3px solid ${THEME.primary}` : '2px solid rgba(255,255,255,0.2)',
                      background: isCorrectAnswer
                        ? 'rgba(52, 211, 153, 0.3)'
                        : isWrongAnswer
                        ? 'rgba(239, 68, 68, 0.3)'
                        : isSelected
                        ? THEME.primaryLight
                        : 'rgba(248, 250, 252, 0.2)',
                      color: 'white',
                      textAlign: 'center',
                      fontSize: '1.1rem',
                    }}
                  >
                    <span style={{ marginRight: '0.5rem', opacity: 0.7 }}>
                      {String.fromCharCode(65 + index)}.
                    </span>
                    {option}
                  </button>
                );
              })}
            </div>
          )}

          {/* Host Playing Feedback */}
          {hostIsPlaying && answerSubmitted && (
            <div style={{
              background: answerCorrect ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              border: `3px solid ${answerCorrect ? 'rgba(52, 211, 153, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              borderRadius: '12px',
              padding: '1.5rem',
              textAlign: 'center',
              marginTop: '1rem',
            }}>
              {answerCorrect ? (
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#34D399' }}>
                  🎉 Correct! +{pointsEarned} points
                </div>
              ) : (
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#EF4444' }}>
                  🍺 Wrong! Take {sipsEarned} {sipsEarned === 1 ? 'sip' : 'sips'}!
                </div>
              )}
            </div>
          )}
        </div>
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.accent,
                accentLight: THEME.accentLight,
                accentBorder: THEME.accentBorder,
              }}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              isMobile={true}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.accent,
                accentLight: THEME.accentLight,
                accentBorder: THEME.accentBorder,
              }}
            />
          }
          rightWidth="320px"
        />
      </div>
    </PartyHostPlayingChrome>
  );
};

export default DrinkingGameHostDisplay;
